import { Link } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { CheckCircle2, Circle, CircleDot, AlertOctagon, PlayCircle } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Pill } from '@/features/feeds/components/FeedsNav'
import type { PlanState, PlanStep } from '../api/growthApi'
import { ErrorNote, GrowthFrame, money, num, useSelection } from '../components/GrowthFrame'
import { useStrategy } from '../hooks/useGrowth'
import { CLASS_LABEL } from './SearchTermsPage'

// Status ships with an icon and a word, never color alone.
const STATE: Record<PlanState, { label: string; tone: 'green' | 'blue' | 'amber' | 'red' | 'slate'; icon: React.ReactNode }> = {
  live: { label: 'Live', tone: 'green', icon: <CheckCircle2 className="h-4 w-4 text-emerald-600" /> },
  ready: { label: 'Ready — our move', tone: 'blue', icon: <PlayCircle className="h-4 w-4 text-sky-600" /> },
  waiting: { label: 'Waiting on evidence', tone: 'amber', icon: <CircleDot className="h-4 w-4 text-amber-600" /> },
  blocked: { label: 'Blocked', tone: 'red', icon: <AlertOctagon className="h-4 w-4 text-red-600" /> },
  planned: { label: 'Planned', tone: 'slate', icon: <Circle className="h-4 w-4 text-muted-foreground" /> },
}

export function PlanList({ steps }: { steps: PlanStep[] }) {
  return (
    <ol className="divide-y">
      {steps.map((s) => {
        const st = STATE[s.state] || STATE.planned
        return (
          <li key={s.key} className="flex gap-3 py-2.5">
            <div className="pt-0.5">{st.icon}</div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{s.title}</span>
                <Pill tone={st.tone}>{st.label}</Pill>
              </div>
              <div className="text-sm text-muted-foreground">{s.reading}</div>
              {s.next && <div className="text-xs text-muted-foreground">Next: {s.next}</div>}
            </div>
          </li>
        )
      })}
    </ol>
  )
}

function Row({ label, children, warn }: { label: string; children: React.ReactNode; warn?: boolean }) {
  return (
    <div className="grid grid-cols-[12rem_1fr] gap-3 py-1.5 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={warn ? 'text-amber-800' : ''}>{warn && '⚠ '}{children}</span>
    </div>
  )
}

export function StrategyPage() {
  const sel = useSelection()
  const q = useStrategy()
  const s = q.data
  const name = (id: string) => sel.accounts.find((a) => a.customerId === id)?.name || id
  const currencyOf = (id: string) => sel.accounts.find((a) => a.customerId === id)?.currency || 'USD'

  return (
    <GrowthFrame
      hideWindow
      title="Strategy"
      description="The growth plan's steps against the evidence each waits for, and the health of every signal feeding Google — conversions, checkouts, intent scores, the nightly miner and the sales tiers. Read across all markets, last 24 hours unless stated."
    >
      {q.error && <ErrorNote error={q.error} />}
      {!s ? <Skeleton className="h-96" /> : (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Growth plan</CardTitle>
              <CardDescription>
                Steps and thresholds are code (lib/ads/digest.js) so a change to the plan is reviewed. Break-even ROAS {s.breakEven}×. Read {formatDistanceToNow(new Date(s.readAt), { addSuffix: true })}.
              </CardDescription>
            </CardHeader>
            <CardContent><PlanList steps={s.plan} /></CardContent>
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader><CardTitle className="text-base">Conversion signals</CardTitle><CardDescription>What we tell Google happened, from the server.</CardDescription></CardHeader>
              <CardContent className="divide-y">
                {s.tracking && (() => {
                  const o = s.tracking.orders
                  const total = o.reduce((t, r) => t + r.n, 0)
                  const sent = o.filter((r) => r.status === 'sent').reduce((t, r) => t + r.n, 0)
                  return (
                    <>
                      <Row label="Paid orders">
                        {total ? `${total} — ${sent} sent as Purchase (server)` : 'none in 24 h'}
                        {o.filter((r) => r.status !== 'sent').map((r) => <div key={r.status + r.reason} className="text-xs text-muted-foreground">{r.n} {r.status}{r.reason ? ` (${r.reason})` : ''}</div>)}
                      </Row>
                      <Row label="Ad-click checkouts" warn={s.tracking.checkouts.failed > 0}>
                        {s.tracking.checkouts.sent} sent as Begin checkout{s.tracking.checkouts.failed ? ` · ${s.tracking.checkouts.failed} failed` : ''}
                      </Row>
                    </>
                  )
                })()}
                {s.intent && (
                  <>
                    <Row label={`Intent scores (${s.intent.mode})`} warn={!s.intent.scores}>
                      {s.intent.scores ? `${num(s.intent.scores)} visitors · ${s.intent.scoresToday} in 24 h` : 'none received yet — waits on the NeuroGraph job'}
                    </Row>
                    <Row label="Qualified visits">{s.intent.qualifiedToday} in 24 h · {s.intent.qualified30d} in 30 days</Row>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Query mining</CardTitle>
                <CardDescription>The nightly sync and what it proposes. <Link className="underline" to="/app/feeds/proposals">Review proposals →</Link></CardDescription>
              </CardHeader>
              <CardContent className="divide-y">
                {s.mining && (
                  <>
                    {s.mining.runs.map((r) => (
                      <Row key={r.customer_id} label={`Sync · ${name(r.customer_id)}`} warn={r.status !== 'ok'}>
                        {r.status} {formatDistanceToNow(new Date(r.started_at), { addSuffix: true })}{r.error ? ` — ${r.error.slice(0, 120)}` : ''}
                      </Row>
                    ))}
                    <Row label="Negatives applied (24 h)">{s.mining.applied.map((r) => `${name(r.customer_id)} ${r.n}`).join(' · ') || 'none'}</Row>
                    <Row label="Waiting for review">
                      {s.mining.waiting.map((r) => `${name(r.customer_id)}: ${r.n} ${r.kind.replace('_', ' ')}`).join(' · ') || 'nothing'}
                    </Row>
                    <Row label="No-sale spend (7 d)">
                      {s.mining.wasted.length ? s.mining.wasted.map((w) => (
                        <div key={w.customer_id + w.term}>"{w.term}" {money(w.cost, currencyOf(w.customer_id))} on {w.clicks} clicks <span className="text-xs text-muted-foreground">{CLASS_LABEL[w.class] || w.class}</span></div>
                      )) : 'none'}
                    </Row>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Products and labels in Google Ads</CardTitle>
              <CardDescription>
                What Ads can actually serve. "Servable" counts products some campaign targets and Google approved — a campaign with no impressions
                usually has nothing servable, not bad bids. Merchant Center can also show a label Ads never receives, and a product group on such a
                label silently matches nothing.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {(s.labels || []).map((acct) => (
                <div key={acct.customerId}>
                  <div className="mb-1 text-xs font-medium text-muted-foreground">
                    {name(acct.customerId)} · {acct.destination || 'no feed'}
                    {acct.products && (
                      <span className={acct.products.ELIGIBLE ? '' : 'text-red-700'}>
                        {' '}· {num(acct.products.ELIGIBLE || 0)} servable of {num(Object.values(acct.products).reduce((t, n) => t + n, 0))} products
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {acct.labels.map((l) => (
                      <span key={l.label} title={`${l.feedItems.toLocaleString()} feed items send it`}
                        className={`rounded-md border px-2 py-1 text-xs ${l.missing ? 'border-red-300 bg-red-50 text-red-800' : l.feedItems ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-border text-muted-foreground'}`}>
                        {l.missing ? '✕' : l.feedItems ? '✓' : '–'} {l.label.replace('custom_label_', 'label ')} · {l.meaning}
                        {l.missing ? ' — not in Ads' : !l.feedItems ? ' — not sent' : ''}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
              {!(s.labels || []).length && <p className="text-sm text-muted-foreground">Not checked.</p>}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Sales tiers</CardTitle>
              <CardDescription>
                hero = 5+ units, core = any sale. Each market moves on its own sales since {s.tiersCutover}; promotions are nightly, demotions weekly (Mondays) so campaigns are not reshuffled while Google learns.
              </CardDescription>
            </CardHeader>
            <CardContent className="divide-y">
              {(s.tiers || []).map((t) => (
                <Row key={t.destination} label={t.destination}>
                  {t.hero} hero · {t.core} core · {t.promoted} up, {t.demoted} down in 24 h{t.held ? ` · ${t.held} demotions held for Monday` : ''}
                </Row>
              ))}
            </CardContent>
          </Card>

          {s.errors.length > 0 && <ErrorNote error={{ message: s.errors.join(' · ') }} />}
        </>
      )}
    </GrowthFrame>
  )
}
